import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import consumerApi from "api/services/consumer-api";
import reservationApi from "api/services/reservations";
import { Cart } from "custom-types/Cart";
import { MenuItem } from "custom-types/MenuItem";
import { MenuItemV2 } from "custom-types/MenuItemV2";
import { VariantWithDeliveryInfo } from "custom-types/Variant";
import { useATBStats } from "hooks/useATBStats";
import { useEventTracker } from "hooks/useEventTracker";
import {
  LoadCartAction,
  RESERVATION_LOAD_CART,
} from "redux/reducers/reservation";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCartId } from "redux/selectors/reservation";
import { Action, Category, Label } from "utils/trackEvent";

import { AddToBagModal } from "components/AddToBagModal";
import Button from "components/botanic/Button";
import { useIsJaneCheckoutEnabled } from "components/Checkout/JaneCheckout";

type AddToBagButtonProps = {
  buttonSize?: string;
  defaultVariantId: number;
  immediatelyAddToBag?: boolean;
  menuItem: MenuItem | MenuItemV2 | VariantWithDeliveryInfo;
  quantity?: number;
  onClick?: () => void;
  onReservation?: () => void;
  splitClickEventName?: string;
  ctaText?: string;
  addingToBagText?: string;
  successText?: string;
  variant?: string;
  merchandisingCampaignId?: number;
};

export const AddToBagButton: React.FC<AddToBagButtonProps> = ({
  buttonSize = "small",
  ctaText = "add to bag",
  addingToBagText = "adding",
  successText = "added!",
  variant,
  defaultVariantId,
  immediatelyAddToBag = false,
  menuItem,
  quantity,
  onClick = () => {},
  onReservation = () => {},
  splitClickEventName,
  merchandisingCampaignId,
}) => {
  const cartId = useSelector(getCartId) as string; // You will have a cart id by the time you open the modal
  const countryCode = useSelector(getDomainCountryCode);
  const dispatch = useDispatch();
  const [viewingModal, setViewingModal] = useState(false);
  const buttonRef = useRef(null);
  const { reportATBStats } = useATBStats();
  const isJaneCheckoutEnabled = useIsJaneCheckoutEnabled();
  const { publishEvent } = useEventTracker();

  const [isAddingToBag, setIsAddingToBag] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  useEffect(() => {
    if (showSuccessMessage) {
      setIsAddingToBag(false);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 1200);
    }
  }, [showSuccessMessage]);
  const addToBag = () => {
    setIsAddingToBag(true);
    onClick();
    publishEvent({
      action: Action.click,
      cartId,
      category: Category.addToBag,
      janeCheckoutEnabled: isJaneCheckoutEnabled,
      label: Label.addToBag,
      menuItemId: menuItem.id,
      merchandisingCampaignId: merchandisingCampaignId,
      productId: defaultVariantId,
    });
    setViewingModal(true);
  };
  // Once the AddToBagModal renders, it handles adding the menu item to bag in a useEffect
  return (
    <>
      <Button
        data-testid="add-to-bag-button"
        iconFilePath="bag.svg"
        disabled={showSuccessMessage}
        onClick={addToBag}
        size={buttonSize}
        className={classNames("w-full disabled", {
          "py-sm": buttonSize === "small",
        })}
        ref={buttonRef}
        variant={variant}
        loadingText={addingToBagText}
        isLoading={isAddingToBag}
      >
        {showSuccessMessage ? successText : ctaText}
      </Button>

      {viewingModal && (
        <AddToBagModal
          cartId={cartId}
          quantity={quantity}
          consumerApiUrl={consumerApi.defaults.baseURL as string}
          countryCode={countryCode}
          onCartFetch={(cart: Cart) => {
            dispatch<LoadCartAction>({
              cart,
              type: RESERVATION_LOAD_CART,
            });

            if (typeof document !== "undefined") {
              // Signal to cephalopod to refresh the cart
              document.dispatchEvent(new CustomEvent("getcart"));
            }
          }}
          onDismiss={() => {
            setViewingModal(false);
            setIsAddingToBag(false);
          }}
          onReservation={() => {
            reportATBStats({ atbAction: true, atbError: false });
            setShowSuccessMessage(true);
            onReservation();
          }}
          onReservationFail={() => {
            setIsAddingToBag(false);
            reportATBStats({ atbAction: true, atbError: true });
          }}
          reservationsApiUrl={reservationApi.defaults.baseURL as string}
          returnFocusRef={buttonRef}
          splitEventName={splitClickEventName}
          useAddedToBagPopout={true}
          immediatelyAddToBag={immediatelyAddToBag}
          variantId={defaultVariantId}
        />
      )}
    </>
  );
};
